import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Edit, Plus, GripVertical } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface FAQ {
  id: string;
  question: string;
  answer: string;
  display_order: number | null;
  is_active: boolean | null;
}

interface SortableFAQItemProps {
  faq: FAQ;
  onEdit: (faq: FAQ) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
}

function SortableFAQItem({ faq, onEdit, onDelete, onToggleActive }: SortableFAQItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing mt-1"
          >
            <GripVertical className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <div className="flex items-start gap-2 mb-2">
              <h4 className="font-semibold flex-1">{faq.question}</h4>
              <Badge variant={faq.is_active ? "default" : "secondary"}>
                {faq.is_active ? "Ativo" : "Inativo"}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">{faq.answer}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <Switch
                checked={faq.is_active ?? true}
                onCheckedChange={(checked) => onToggleActive(faq.id, checked)}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(faq)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(faq.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function FAQManager() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ question: "", answer: "" });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadFaqs();
  }, []);

  const loadFaqs = async () => {
    try {
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      const mappedFaqs = (data || []).map(faq => ({
        ...faq,
        display_order: faq.display_order ?? 0,
        is_active: faq.is_active ?? true
      }));
      setFaqs(mappedFaqs);
    } catch (error) {
      console.error('Error loading FAQs:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar FAQs",
        variant: "destructive",
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = faqs.findIndex((faq) => faq.id === active.id);
    const newIndex = faqs.findIndex((faq) => faq.id === over.id);

    const newFaqs = arrayMove(faqs, oldIndex, newIndex);
    setFaqs(newFaqs);

    // Update display_order for all FAQs
    try {
      const updates = newFaqs.map((faq, index) => 
        supabase
          .from('faqs')
          .update({ display_order: index })
          .eq('id', faq.id)
      );

      await Promise.all(updates);
      toast({ title: "Ordem atualizada com sucesso!" });
    } catch (error) {
      console.error('Error updating order:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar ordem",
        variant: "destructive",
      });
      loadFaqs(); // Reload to restore correct order
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const sanitizedQuestion = formData.question.trim().slice(0, 500);
    const sanitizedAnswer = formData.answer.trim().slice(0, 2000);
    
    if (!sanitizedQuestion || !sanitizedAnswer) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos",
        variant: "destructive",
      });
      return;
    }

    if (sanitizedQuestion.length < 10) {
      toast({
        title: "Erro",
        description: "A pergunta deve ter pelo menos 10 caracteres",
        variant: "destructive",
      });
      return;
    }

    if (sanitizedAnswer.length < 20) {
      toast({
        title: "Erro",
        description: "A resposta deve ter pelo menos 20 caracteres",
        variant: "destructive",
      });
      return;
    }

    try {
      if (isEditing) {
        const { error } = await supabase
          .from('faqs')
          .update({ 
            question: sanitizedQuestion, 
            answer: sanitizedAnswer 
          })
          .eq('id', isEditing);
        
        if (error) throw error;
        toast({ title: "FAQ atualizada com sucesso!" });
      } else {
        const { error } = await supabase
          .from('faqs')
          .insert({ 
            question: sanitizedQuestion, 
            answer: sanitizedAnswer,
            display_order: faqs.length,
            is_active: true
          });
        
        if (error) throw error;
        toast({ title: "FAQ criada com sucesso!" });
      }

      setFormData({ question: "", answer: "" });
      setIsEditing(null);
      setShowForm(false);
      loadFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar FAQ",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (faq: FAQ) => {
    setFormData({ question: faq.question, answer: faq.answer });
    setIsEditing(faq.id);
    setShowForm(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase
        .from('faqs')
        .delete()
        .eq('id', deleteId);
      
      if (error) throw error;
      toast({ title: "FAQ excluída com sucesso!" });
      loadFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir FAQ",
        variant: "destructive",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('faqs')
        .update({ is_active: isActive })
        .eq('id', id);
      
      if (error) throw error;
      toast({ 
        title: isActive ? "FAQ ativada!" : "FAQ desativada!",
        description: isActive 
          ? "A pergunta agora é visível no site" 
          : "A pergunta foi ocultada do site"
      });
      loadFaqs();
    } catch (error) {
      console.error('Error toggling FAQ:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar status",
        variant: "destructive",
      });
    }
  };

  const isFormValid = 
    formData.question.trim().length >= 10 && 
    formData.answer.trim().length >= 20;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Gerenciar FAQ</h3>
        {!showForm && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Adicionar FAQ
          </Button>
        )}
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{isEditing ? "Editar" : "Nova"} FAQ</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Pergunta
                  <span className="text-muted-foreground ml-2">
                    ({formData.question.length}/500)
                  </span>
                </label>
                <Input
                  value={formData.question}
                  onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="Digite a pergunta (mínimo 10 caracteres)"
                  maxLength={500}
                  required
                />
                {formData.question.trim().length > 0 && formData.question.trim().length < 10 && (
                  <p className="text-sm text-destructive mt-1">
                    Mínimo 10 caracteres
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Resposta
                  <span className="text-muted-foreground ml-2">
                    ({formData.answer.length}/2000)
                  </span>
                </label>
                <Textarea
                  value={formData.answer}
                  onChange={(e) => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                  placeholder="Digite a resposta (mínimo 20 caracteres)"
                  rows={4}
                  maxLength={2000}
                  required
                />
                {formData.answer.trim().length > 0 && formData.answer.trim().length < 20 && (
                  <p className="text-sm text-destructive mt-1">
                    Mínimo 20 caracteres
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={!isFormValid}>
                  {isEditing ? "Atualizar" : "Criar"}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowForm(false);
                    setIsEditing(null);
                    setFormData({ question: "", answer: "" });
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {faqs.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              Nenhuma FAQ encontrada. Adicione a primeira!
            </CardContent>
          </Card>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={faqs.map(faq => faq.id)}
              strategy={verticalListSortingStrategy}
            >
              {faqs.map((faq) => (
                <SortableFAQItem
                  key={faq.id}
                  faq={faq}
                  onEdit={handleEdit}
                  onDelete={(id) => setDeleteId(id)}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta FAQ? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
